import type { App, AsyncComponentLoader, ComponentPublicInstance } from 'vue'

import type { Plugin } from './plugin'
import { usePlugin } from './plugin'

export type Application = {
  component: AsyncComponentLoader
  container: Array<HTMLElement | null> | NodeListOf<HTMLElement>
  plugins?: Plugin[]
  configureApp?: (args: { app: App; container: HTMLElement }) => Promise<void>
  configureInstance?: (args: { app: App; vm: ComponentPublicInstance }) => Promise<void>
}

export const createApplication = (application: Application) => application

const applicationFactory = async (
  container: HTMLElement,
  application: Application,
  _plugins: Plugin[]
): Promise<void> => {
  // merge global and local plugins
  const plugins = [
    ..._plugins, // global plugins
    ...(application.plugins || []) // local plugins
  ]

  // load all required pieces at once
  const [{ createApp }, component, ...vuePlugins] = await Promise.all([
    import('vue'),
    application.component(),
    ...plugins.map((plugin) => plugin.load())
  ])

  // create the application instance
  const app = createApp(component.default || component)

  // apply the plugins
  vuePlugins.forEach((plugin) => usePlugin(app, plugin))

  // notify the application before mounting
  await application.configureApp?.({ app, container })

  // mount the application instance
  const vm = app.mount(container)

  // notify the application after mounting
  await application.configureInstance?.({ vm, app })
}

export const buildApplicationFactories = (
  applications: Application[],
  plugins: Plugin[]
): Array<() => void> => {
  const applicationFactories = []

  for (const application of applications) {
    for (const container of application.container) {
      container &&
        applicationFactories.push(applicationFactory.bind(null, container, application, plugins))
    }
  }

  // if we do not have any factory, we can return early
  if (applicationFactories.length === 0) {
    return []
  }

  return applicationFactories
}
